import { faLocationDot } from "@fortawesome/free-solid-svg-icons"
import { makeStyles, Typography } from "@material-ui/core"
import ChartCard from "../../molecules/charts/ChartCard"
import StackedDotPlot, {
  StackedDotPlotSeries,
} from "../../molecules/charts/StackedDotPlot"
import { colors } from "../../../utils/theme"
import {
  getMyPlacesTrendCaption,
  MyPlacesChartDatum,
} from "../../../utils/myPlacesTrend"

const SERIES: StackedDotPlotSeries<MyPlacesChartDatum>[] = [
  {
    key: "riskyPlaces",
    label: "Risky Places",
    valueKey: "riskyPlaces",
    color: colors.graphs.myPlaces.riskyPlace,
  },
  {
    key: "safePlaces",
    label: "Safe Places",
    valueKey: "safePlaces",
    color: colors.graphs.myPlaces.safePlace,
  },
]

const MIN_CHART_HEIGHT = 160
const ROW_HEIGHT = 26
const GROUP_OFFSET = 0.3

/**
 * Renders the MyPlaces stacked dot plot chart.
 */
interface MyPlacesChartProps {
  data: MyPlacesChartDatum[]
  isError: boolean
  isLoading: boolean
}

/** Renders a client's safe and risky place-visit frequency by local day. */
export default function MyPlacesChart({
  data,
  isError,
  isLoading,
}: MyPlacesChartProps) {
  const classes = useStyles()
  const hasData = data.some(
    (datum) => datum.safePlaces > 0 || datum.riskyPlaces > 0
  )

  const maxFrequency = data.reduce(
    (max, { safePlaces, riskyPlaces }) =>
      Math.max(max, safePlaces, riskyPlaces),
    0
  )
  const yTicks = Array.from({ length: maxFrequency + 1 }, (_, index) => index)
  const chartHeight = Math.max(MIN_CHART_HEIGHT, maxFrequency * ROW_HEIGHT)
  const showAxisTitle = !isLoading && !isError && hasData

  return (
    <ChartCard
      icon={faLocationDot}
      iconBgColor={colors.tertiary.orange}
      title="My Places"
    >
      <div className={classes.chartContainer}>
        <div className={classes.chartHeader}>
          {showAxisTitle && (
            <Typography className={classes.chartEyebrow}>Frequency</Typography>
          )}
        </div>
        {isLoading ? (
          <Typography className={classes.chartMessage} aria-live="polite">
            Loading My Places data…
          </Typography>
        ) : isError ? (
          <Typography className={classes.chartMessage} role="alert">
            Places data could not be loaded.
          </Typography>
        ) : !hasData ? (
          <Typography className={classes.chartMessage}>
            No safe or risky place visits were recorded in the last 14 days.
          </Typography>
        ) : (
          <>
            <StackedDotPlot
              data={data}
              xKey="dayLabel"
              series={SERIES}
              yTickFormatter={(value) => value.toString()}
              yTicks={yTicks}
              height={chartHeight}
              width={1000}
              responsive
              dotSize={7}
              groupOffset={GROUP_OFFSET}
            />
            <Typography className={classes.chartCaption}>
              {getMyPlacesTrendCaption(data)}
            </Typography>
          </>
        )}
      </div>

      <div className={classes.legend}>
        {SERIES.map((series) => (
          <div key={series.key} className={classes.legendItem}>
            <span
              className={classes.legendDot}
              style={{ backgroundColor: series.color }}
            />
            <Typography className={classes.legendText}>
              {series.label}
            </Typography>
          </div>
        ))}
      </div>
    </ChartCard>
  )
}

const useStyles = makeStyles({
  chartContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
  },
  chartHeader: {
    display: "flex",
    alignItems: "center",
    minHeight: 16,
  },
  chartEyebrow: {
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 400,
    color: colors.graphs.labels.colorText,
    letterSpacing: "0.08em",
  },
  chartCaption: {
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 400,
    color: colors.graphs.labels.colorText,
    textAlign: "center",
    textTransform: "uppercase",
  },
  chartMessage: {
    minHeight: 160,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: colors.graphs.labels.colorText,
  },
  legend: {
    display: "flex",
    justifyContent: "center",
    gap: 24,
    marginTop: 12,
    flexWrap: "wrap",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    display: "inline-block",
  },
  legendText: {
    color: colors.graphs.labels.colorText,
    fontSize: 12,
    lineHeight: "16px",
  },
})
